export const aviationBvrs = [
  {
    behaviourKey: "OPTIMAL_DISCRETIONARY_FUEL_LOAD",
    behaviourAcronym: "ODFL",
    behaviourName: "Optimal Discretionary Fuel Load",
    description:
      "Calculate and load only the fuel required to account for changes in zero fuel weight, within a 200kg buffer",
    reasons: `• Missing or faulty data fields (e.g. invalid fuel tank data, outlier values).
    • Tankered flight.`,
    terminology: `<p><strong>Expected block-off fuel</strong> is calculated using the flight plan and zero fuel weight adjustment.</p><p><br></p><p><strong>Discretionary fuel loaded</strong> is the difference between the expected block off fuel and the actual block off fuel.</p><p><br></p><p><strong>Discretionary fuel needed</strong> is any fuel that was needed beyond the contingency and arrival delay fuel.</p><p><br></p><p><strong>350kg threshold</strong> is a value agreed with your airline to account for factors which make it unrealistic to calculate and load the precise amount of required fuel.</p>`,
    fuelSavingsSource: `Your fuel saving is calculated as the fuel burn avoided by carrying less than the expected fuel plus a 200kg buffer`,
    order: 3,
  },
  {
    behaviourKey: "EFFICIENT_FLIGHT",
    behaviourAcronym: "EF",
    behaviourName: "Efficient Flight",
    description: "Burn no more than the planned trip fuel",
    reasons: `• Missing or faulty data fields (e.g. invalid TOW, outlier values).
      • Flight duration is extended by more than 20 minutes.`,
    terminology: `<p><strong>Expected trip fuel </strong>is the trip fuel according to the flight plan, with adjustments made to account for changes in takeoff weight.</p><p><br></p><p><strong>Actual trip fuel</strong> is calculated from the actual fuel values at takeoff and landing.</p>`,
    fuelSavingsSource:
      "Your fuel saving is the difference between the planned trip fuel, adjusted for changes in weight, and the actual trip fuel.",
    order: 4,
  },
  {
    behaviourKey: "REDUCED_ENGINE_TAXI_OUT",
    behaviourAcronym: "RETO",
    behaviourName: "Reduced Engine Taxi Out",
    description:
      "Ensure one engine burns less than 80% of the fuel burned by the other engine during taxi out",
    reasons: `• Missing or faulty data fields (e.g. missing fuel values).
    • Taxi-out duration is shorter than or equal to engine warm up time.
    • Taxi-out duration is over 180 minutes or 1.2 times projected taxi time.`,
    fuelSavingsSource: `Your fuel saving is calculated as the difference between the fuel burned in each engine before engine warm up.
    Performing RETO typically saves over 100kg of fuel per flight.`,
    order: 1,
  },
  {
    behaviourKey: "REDUCED_ENGINE_TAXI_IN",
    behaviourAcronym: "RETI",
    behaviourName: "Reduced Engine Taxi In",
    description:
      "Ensure one engine burns less than 80% of the fuel burned by the other engine during taxi in",
    reasons: `• Missing or faulty data fields (e.g. missing fuel values)
    • Taxi-in duration is shorter than or equal to engine cool down time.
    • Taxi-in duration is over 180 minutes or 1.2 times projected taxi time`,
    fuelSavingsSource: `Your fuel saving is calculated as the difference between the fuel burned in each engine after engine cool down.
    Performing RETI saves an average of 80kg of fuel per flight.`,
    order: 2,
  } /*,
  {
    behaviourKey: 'CONTINUOUS_DESCENT',
    behaviourAcronym: 'CD',
    behaviourName: 'Continuous Descent',
    description: 'Spend less than 10% of your descent at level flight',
    reasons: `• Missing or faulty data fields (e.g. missing fuel values).`,
    fuelSavingsSource:
      'When successful, you save extra fuel for every additional second you spend descending. The extra fuel is the difference between the mean fuel flow while descending and a calculated fuel flow at level flight.',
    order: 0
  },
  {
    behaviourKey: 'DELAYED_FLAPS_APPROACH',
    behaviourAcronym: 'DFA',
    behaviourName: 'Delayed Flaps Approach',
    description: 'Delay deployment of flaps during the approach phase',
    reasons: `• Missing or faulty data fields (e.g. missing fuel values).`,
    fuelSavingsSource:
      'For every nautical mile you delay the target flaps deployment past the threshold, your fuel savings increase by a calculated figure. The associated figure varies by aircraft. ',
    order: 5
  }*/,
];

export const shippingBvrs = [
  {
    behaviourTypeId: "1",
    behaviourKey: "PROMPT_DEPARTURE",
    behaviourAcronym: "PD",
    behaviourName: "Prompt Departure",
    description:
      "Ensure Actual Time of Departure from the ship's berth is within 2.5 hours of last cargo hose disconnection",
    reasons: `• Missing required values. e.g. atd, cargo docs on board time and hose disconnected time
    • Time taken between hose disconnection and departure time > 8 hours
    • Time between hose disconnection to cargo docs onboard > 3 hours`,
    fuelSavingsSource:
      "Your fuel saving is calculated by comparing the time between your Actual Time of Departure and the cargo hose disconnection to the 2.5 hour threshold. For any time you save by departing before this 2.5 hour threshold, you will save an average of 75kg per hour on the auxiliary engines and auxiliary boilers.",
    order: 4,
  },
  {
    behaviourTypeId: "2",
    behaviourKey: "EFFICIENT_AUXILIARY_ENGINE_USE",
    behaviourAcronym: "EAEU",
    behaviourName: "Efficient Auxiliary Engine Use",
    description: `Limit excess running hours to 1 hour or less by loading the minimum number of auxiliary engines to meet demand.`,
    reasons: `• When the vessel is manoeuvring.
    • When the data appears faulty e.g. AE hours > 30.
    • When some data is missing e.g. generator running hours
    • When the auxiliary engine is not in use`,
    fuelSavingsSource: `When your excess auxiliary engine running hours are less than 1 hour, your average hourly fuel oil consumption on the auxiliary engines is applied to the running time you saved.`,
    order: 5,
  },
  {
    behaviourTypeId: "3",
    behaviourKey: "OPTIMAL_TRIM",
    behaviourAcronym: "OT",
    behaviourName: "Optimal Trim",
    description: "Trim the vessel according to the vessel's trim table",
    reasons: `• When the vessel is not at sea
• When speed through water is < 7 knots or > 14 knots
• When Main Engine is not running
• When some data is missing e.g. observed distance
• When the data appears faulty e.g. mean draft > 10
• When wind speed is ≥ BF5`,
    fuelSavingsSource:
      "For every percentage point you deviate from the vessel's historical trim towards the vessel's optimal trim, your fuel savings increase by an associated percentage. The associated percentage varies by vessel.",
    order: 3,
  },
  {
    behaviourTypeId: "4",
    behaviourKey: "EFFICIENT_SFOC",
    behaviourAcronym: "ESFOC",
    behaviourName: "Efficient SFOC",
    description: "While underway, meet the vessel’s expected ME SFOC value.",
    reasons: `• When the vessel is not at sea
    • When the vessel is at a low RPM or speed is < 6kts
    • When the vessel is manoeuvring or not sailing
    • When the data appears faulty e.g SFOC < 100 g/kwh
    • When some data is missing e.g. main engine running hours`,
    fuelSavingsSource:
      "Your fuel saving is the difference between the actual ME Specific Fuel Oil Consumption (SFOC) and the expected ME SFOC value from your ship's technical manager.",
    order: 1,
    terminology: `<strong>Expected ME SFOC</strong> is the value provided by your ship technical manager, with an additional buffer added to ensure fairness.`,
  },
];

const baseUserProfileAviation = {
  appUser: {
    id: "deadbeef-eb89-4616-b824-d139987d3fd8",
    identityId: "deadbeef-eb89-4616-b824-d139987d3fd8",
    createdAt: 1683849600352,
    //"email": '',
    username: null,
    firstname: "Alan",
    lastname: "Rogers",
    dataPrivacyAgreement: 1737417600000,
    dataPrivacyAgreementSigned: true,
  },
  operator: {
    id: "beefbabe-84fd-436d-b152-bfdf325c997c",
    code: "162342",
    secondaryCode: null,
    active: true,
    milestoneDonationSeed: null,
    milestoneDonationValue: null,
    milestoneFuelSeed: 300,
    milestoneFuelValue: 15900,
    enrolledDate: new Date(
      new Date().getFullYear() - 2,
      new Date().getMonth(),
      13,
    ),
    lastEventDate: new Date(
      new Date().getFullYear(),
      new Date().getMonth() - 1,
      25,
    ),
    lastReceivedData: new Date(
      new Date().getFullYear(),
      new Date().getMonth(),
      new Date().getDate() - 2,
    ),
    createdAt: new Date(
      new Date().getFullYear() - 2,
      new Date().getMonth(),
      10,
    ),
  },
  behaviours: [...aviationBvrs],
  team: {
    targetBehaviours: aviationBvrs.map((b) => ({
      acronym: b.behaviourAcronym,
      baselineDateSource: "customer_contract",
    })),
  },
  customer: {
    preferredEventName: "flight",
    preferredEventNamePlural: "flights",
    customerCode: "DA",
    name: "Demo Air",
    industry: "aviation",
    language: "en",
    targetPeriodDuration: "MONTHLY",
    enabledPredictions: true,
    reportingDelay: 3,
    contractStartDate: new Date(
      new Date().getFullYear() - 2,
      new Date().getMonth(),
      10,
    ),
    contractEndDate: null,
    enrollmentType: null,
    isDeleted: false,
    quicksightDashboardId: null,
    periodLabel: "month",
  },
  charities: [
    {
      description:
        "<div> a registered charity that helps current and former aviators and BALPA employees plus their families who need support.</div>",
      donationAmount: 10,
      donationStatement: "donate $10",
      id: "charityone",
      name: "BALPA Benevolent Fund",
      url: "https://www.balpa.org/benevolent-fund/",
      videoId: "Uoq2EG3BpS4",
    },
  ],
  dataPrivacyAgreement: false,
  preferredLanguage: "en",
  operatorMetadata: [
    {
      key: "active",
      value: "true",
      type: "BOOLEAN",
    },
    {
      key: "onboarding_commitment",
      value: "ENVIRONMENT",
      type: "STRING",
      modifiedAt: "2024-11-23T00:00:00.000Z",
    },
    {
      key: "role_alias",
      value: "Captain",
      type: "STRING",
    },
  ],
};

const baseUserProfileMaritime = {
  appUser: {
    id: "deadbeef-91fa-4ecd-af1c-aa659e08dbab",
    identityId: "deadbeef-91fa-4ecd-af1c-aa659e08dbab",
    createdAt: 1683849600352,
    //"username": '',
    email: null,
    firstname: "Julian",
    lastname: "Vidal",
    dataPrivacyAgreement: 1737417600000,
    dataPrivacyAgreementSigned: true,
  },
  operator: {
    id: "beefbabe-84fd-436d-b152-bfdf325c997c",
    code: "162342",
    secondaryCode: null,
    active: true,
    milestoneDonationSeed: null,
    milestoneDonationValue: null,
    milestoneFuelSeed: 300,
    milestoneFuelValue: 15900,
    enrolledDate: new Date(
      new Date().getFullYear() - 2,
      new Date().getMonth(),
      13,
    ),
    lastEventDate: new Date(
      new Date().getFullYear(),
      new Date().getMonth() - 1,
      25,
    ),
    lastReceivedData: new Date(
      new Date().getFullYear(),
      new Date().getMonth(),
      new Date().getDate() - 2,
    ),
    createdAt: new Date(
      new Date().getFullYear() - 2,
      new Date().getMonth(),
      10,
    ),
  },
  customer: {
    preferredEventName: "opportunity",
    preferredEventNamePlural: "opportunities",
    id: "71e4fbc7-0530-41af-b5ab-6a801625bd8d",
    customerCode: "DEL",
    name: "Demo Line",
    industry: "shipping",
    language: "en",
    corporateEmails: "",
    baselinePeriod: 3,
    targetPeriodDuration: "BIWEEKLY",
    enabledPredictions: false,
    contractStartDate: new Date(
      new Date().getFullYear() - 2,
      new Date().getMonth(),
      10,
    ),
    contractEndDate: null,
    enrollmentType: null,
    isDeleted: false,
    quicksightDashboardId: null,
    periodLabel: "period",
  },
  charities: [
    {
      description:
        "<div> an international humanitarian medical NGO of French origin best known for its projects in conflict zones and in countries affected by endemic diseases.</div>",
      donationAmount: 10,
      donationStatement: "donate $10",
      id: "charityone",
      name: "Doctors Without Borders",
      url: "https://www.doctorswithoutborders.org/",
      videoId: "Uoq2EG3BpS4",
    },
  ],
  behaviours: [...shippingBvrs],
  team: {
    targetBehaviours: shippingBvrs.map((b) => ({
      acronym: b.behaviourAcronym,
      baselineDateSource: "customer_contract",
    })),
  },
  operatorMetadata: [
    {
      key: "active",
      value: "true",
      type: "BOOLEAN",
    },
    {
      key: "onboarding_commitment",
      value: "PERSONAL_BESTS",
      type: "STRING",
      modifiedAt: "2024-11-13T00:00:00.000Z",
    },
    {
      key: "role_alias",
      value: "Chief Engineer",
      type: "STRING",
    },
  ],
};

export const getIndustryProfileResp = {
  "spencer@BA.io": {
    ...baseUserProfileAviation,
    appUser: {
      ...baseUserProfileAviation.appUser,
      email: "spencer@BA.io",
      firstname: "Janet",
      lastname: "Brown",
    },
    customer: {
      customerCode: "BA",
      name: "British Airways",
      preferredEventName: "flight",
      preferredEventNamePlural: "flights",
      periodLabel: "month",
      industry: "aviation",
      language: "en",
      targetPeriodDuration: "MONTHLY",
      enabledPredictions: true,
    },
    charities: [],
  },
  "bobby@VS.io": {
    ...baseUserProfileAviation,
    appUser: {
      ...baseUserProfileAviation.appUser,
      email: "bobby@VS.io",
      firstname: "Bobby",
      lastname: "Green",
    },
    customer: {
      customerCode: "DA",
      name: "Demo Air",
      preferredEventName: "flight",
      preferredEventNamePlural: "flights",
      periodLabel: "month",
      industry: "aviation",
      language: "en",
      targetPeriodDuration: "MONTHLY",
      enabledPredictions: true,
    },
    charities: [],
  },
  "jason@VS.io": {
    ...baseUserProfileAviation,
    appUser: {
      ...baseUserProfileAviation.appUser,
      email: "jason@VS.io",
      firstname: "Jason",
      lastname: "Read",
    },

    customer: {
      customerCode: "DA",
      name: "Demo Air",
      preferredEventName: "flight",
      preferredEventNamePlural: "flights",
      periodLabel: "month",
      industry: "aviation",
      language: "en",
      targetPeriodDuration: "MONTHLY",
      enabledPredictions: true,
    },
    charities: [],
  },
  "vlad@SG.io": {
    ...baseUserProfileAviation,
    appUser: {
      ...baseUserProfileAviation.appUser,
      email: "vlad@SG.io",
      firstname: "Alan",
      lastname: "Rogers",
    },
  },
  "captain@SG.io": {
    ...baseUserProfileAviation,
    appUser: {
      ...baseUserProfileAviation.appUser,
      email: "captain@SG.io",
      firstname: "Alan",
      lastname: "Rogers",
    },
    customer: {
      ...baseUserProfileAviation.customer,
      name: "Signol",
    },
  },
  chief1234ard: {
    ...baseUserProfileMaritime,
    appUser: {
      ...baseUserProfileMaritime.appUser,
      username: "chief1234ard",
      firstname: "Viktor",
      lastname: "Marinov",
    },
    charities: [
      {
        description:
          "<div> The Mission to Seafarers is a mission society of the Anglican Communion which offers help and support to merchant seafarers.</div>",
        donationAmount: 10,
        donationStatement: "donate $10",
        id: "charityone",
        name: "The Mission to Seafarers",
        url: "https://www.missiontoseafarers.org/",
        videoId: "Uoq2EG3BpS4",
      },
    ],
  },
  captain123sg: {
    ...baseUserProfileMaritime,
    appUser: {
      ...baseUserProfileMaritime.appUser,
      username: "captain123sg",
      firstname: "Julian",
      lastname: "Vidal",
    },
    metadata: [
      {
        key: "active",
        value: "true",
        type: "BOOLEAN",
      },
      {
        key: "onboarding_commitment",
        value: "PERSONAL_BESTS",
        type: "STRING",
        modifiedAt: "2024-10-22T00:00:00.000Z",
      },
      {
        key: "role_alias",
        value: "Chief Engineer",
        type: "STRING",
      },
    ],
  },
  captainmaritimedemo1: {
    ...baseUserProfileMaritime,
    appUser: {
      ...baseUserProfileMaritime.appUser,
      username: "captainmaritimedemo1",
      firstname: "Julian",
      lastname: "Vidal",
    },
    preferredLanguage: "en",
    dataPrivacyAgreement: true,
  },
  captainmaritimedemo2: {
    ...baseUserProfileMaritime,
    appUser: {
      ...baseUserProfileMaritime.appUser,
      username: "captainmaritimedemo2",
      firstname: "Julian",
      lastname: "Vidal",
    },
  },
  captainmaritimedemo3: {
    ...baseUserProfileMaritime,
    appUser: {
      ...baseUserProfileMaritime.appUser,
      username: "captainmaritimedemo3",
      firstname: "Julian",
      lastname: "Vidal",
    },
  },
  captainmaritimedemo4: {
    ...baseUserProfileMaritime,
    appUser: {
      ...baseUserProfileMaritime.appUser,
      username: "captainmaritimedemo4",
      firstname: "Julian",
      lastname: "Vidal",
    },
  },
  captainmaritimedemo5: {
    ...baseUserProfileMaritime,
    appUser: {
      ...baseUserProfileMaritime.appUser,
      username: "captainmaritimedemo5",
      firstname: "Julian",
      lastname: "Vidal",
    },
  },
  captainmaritimedemo6: {
    ...baseUserProfileMaritime,
    appUser: {
      ...baseUserProfileMaritime.appUser,
      username: "captainmaritimedemo6",
      firstname: "Julian",
      lastname: "Vidal",
    },
  },
  "captainaviationdemo1@sg.io": {
    ...baseUserProfileAviation,
    appUser: {
      ...baseUserProfileAviation.appUser,
      email: "captainaviationdemo1@sg.io",
      firstname: "Alan",
      lastname: "Rogers",
    },

    customer: {
      ...baseUserProfileAviation.customer,
      name: "Signol",
    },
  },
  "captainaviationdemo2@sg.io": {
    ...baseUserProfileAviation,
    appUser: {
      ...baseUserProfileAviation.appUser,
      email: "captainaviationdemo2@sg.io",
      firstname: "Alan",
      lastname: "Rogers",
    },
    customer: {
      ...baseUserProfileAviation.customer,
      name: "Signol",
    },
  },
  "captainaviationdemo3@sg.io": {
    ...baseUserProfileAviation,
    appUser: {
      ...baseUserProfileAviation.appUser,
      email: "captainaviationdemo3@sg.io",
      firstname: "Alan",
      lastname: "Rogers",
    },
    customer: {
      ...baseUserProfileAviation.customer,
      name: "Signol",
    },
  },
  "captainaviationdemo4@sg.io": {
    ...baseUserProfileAviation,
    appUser: {
      ...baseUserProfileAviation.appUser,
      email: "captainaviationdemo4@sg.io",
      firstname: "Alan",
      lastname: "Rogers",
    },
    customer: {
      ...baseUserProfileAviation.customer,
      name: "Signol",
    },
  },
  "captainaviationdemo5@sg.io": {
    ...baseUserProfileAviation,
    appUser: {
      ...baseUserProfileAviation.appUser,
      email: "captainaviationdemo5@sg.io",
      firstname: "Alan",
      lastname: "Rogers",
    },
    customer: {
      ...baseUserProfileAviation.customer,
      name: "Signol",
    },
  },
  "captainaviationdemo6@sg.io": {
    ...baseUserProfileAviation,
    appUser: {
      ...baseUserProfileAviation.appUser,
      email: "captainaviationdemo6@sg.io",
      firstname: "Alan",
      lastname: "Rogers",
    },
    customer: {
      ...baseUserProfileAviation.customer,
      name: "Signol",
    },
  },
};
